<template>
  <Teleport to="body">
  <!-- Floating button -->
  <div class="support-chat-root">
    <button v-if="!open" @click="openSupportChat" class="support-fab" title="AI 小幫手">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
      <span class="support-fab-label">小幫手</span>
    </button>

    <!-- Chat window -->
    <transition name="chat-pop">
      <div v-if="open" class="support-window">
        <!-- Header -->
        <div class="support-header">
          <div class="flex items-center gap-2">
            <div class="support-avatar">🤖</div>
            <div>
              <div class="font-bold text-sm" style="color:#fff;">AI 小幫手</div>
              <div class="text-xs" style="color:rgba(255,255,255,0.7);">系統功能導覽與操作說明</div>
            </div>
          </div>
          <div class="flex items-center gap-1">
            <button @click="clearSupportChat" title="清除對話" class="support-header-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </button>
            <button @click="closeSupportChat" title="關閉" class="support-header-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div class="support-messages" ref="messagesEl">
          <!-- Welcome -->
          <div v-if="!messages.length" class="support-welcome">
            <div class="text-sm font-semibold mb-3" style="color:#4f46e5;">👋 你好！我是 AI 小幫手</div>
            <div class="text-xs mb-4" style="color:#6b7280;">有任何功能操作問題，都可以問我！以下是常見問題：</div>
            <div class="flex flex-wrap gap-2">
              <button v-for="q in quickQuestions" :key="q"
                @click="sendQuick(q)"
                class="support-chip">{{ q }}</button>
            </div>
          </div>

          <!-- Message list -->
          <template v-for="(msg, idx) in messages" :key="idx">
            <div :class="['support-msg', msg.role === 'user' ? 'support-msg-user' : 'support-msg-ai']">
              <div v-if="msg.role === 'assistant'" class="support-msg-avatar">🤖</div>
              <div class="support-msg-bubble" v-html="renderMsg(msg.content)"></div>
            </div>
          </template>

          <!-- Typing indicator -->
          <div v-if="loading" class="support-msg support-msg-ai">
            <div class="support-msg-avatar">🤖</div>
            <div class="support-typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>

        <!-- Input -->
        <div class="support-input-row">
          <textarea
            v-model="inputText"
            @keydown.enter.exact.prevent="send"
            placeholder="輸入您的問題…（Enter 送出，Shift+Enter 換行）"
            class="support-input"
            rows="1"
            ref="inputEl"
          ></textarea>
          <button @click="send" :disabled="!inputText.trim() || loading" class="support-send-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </div>
      </div>
    </transition>
  </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()
const { supportChatOpen: open, supportChatMessages: messages, supportChatLoading: loading } = storeToRefs(store)
const { openSupportChat, closeSupportChat, clearSupportChat, sendSupportMessage } = store

const inputText = ref('')
const messagesEl = ref(null)
const inputEl = ref(null)

const quickQuestions = [
  '如何撰寫並寄出郵件？',
  'AI 草稿功能怎麼用？',
  '如何在郵件內文插入圖片？',
  '如何建立 EDM 行銷活動？',
  '什麼是個人化變數？',
  '怎麼建立行銷推廣頁面？',
  '問卷調查中心怎麼用？',
  '怎麼查看開信率報表？',
]

const send = async () => {
  const text = inputText.value.trim()
  if (!text || loading.value) return
  inputText.value = ''
  await sendSupportMessage(text)
}

const sendQuick = async (q) => {
  await sendSupportMessage(q)
}

// Auto-scroll to bottom
watch(messages, async () => {
  await nextTick()
  if (messagesEl.value) {
    messagesEl.value.scrollTop = messagesEl.value.scrollHeight
  }
}, { deep: true })

watch(open, async (val) => {
  if (val) {
    await nextTick()
    inputEl.value?.focus()
  }
})

// Convert newlines and basic markdown-ish formatting to HTML
const renderMsg = (text) => {
  if (!text) return ''
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`(.+?)`/g, '<code style="background:#f3f4f6;padding:1px 4px;border-radius:3px;font-size:0.85em;">$1</code>')
    .replace(/\n/g, '<br>')
}
</script>

<style>
.support-chat-root {
  position: fixed;
  bottom: 80px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

/* FAB */
.support-fab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-radius: 999px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: #fff;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(79,70,229,0.45);
  font-size: 0.85rem;
  font-weight: 600;
  transition: transform 0.15s, box-shadow 0.15s;
}
.support-fab:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(79,70,229,0.55);
}
.support-fab-label { letter-spacing: 0.02em; }

/* Window */
.support-window {
  width: 360px;
  max-height: 560px;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
  background: #fff;
  border: 1px solid #e5e7eb;
}

/* Header */
.support-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  flex-shrink: 0;
}
.support-avatar {
  width: 32px; height: 32px;
  background: rgba(255,255,255,0.2);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem;
}
.support-header-btn {
  width: 28px; height: 28px;
  border-radius: 6px;
  background: rgba(255,255,255,0.15);
  color: #fff;
  border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s;
}
.support-header-btn:hover { background: rgba(255,255,255,0.28); }

/* Messages */
.support-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f9fafb;
}
.support-welcome {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e5e7eb;
}
.support-chip {
  padding: 5px 10px;
  border-radius: 999px;
  background: #ede9fe;
  color: #4f46e5;
  border: 1px solid #c4b5fd;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background 0.15s;
  text-align: left;
}
.support-chip:hover { background: #ddd6fe; }

.support-msg {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}
.support-msg-user { flex-direction: row-reverse; }
.support-msg-avatar {
  width: 28px; height: 28px;
  background: linear-gradient(135deg,#4f46e5,#7c3aed);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.support-msg-bubble {
  max-width: 80%;
  padding: 9px 13px;
  border-radius: 14px;
  font-size: 0.82rem;
  line-height: 1.55;
}
.support-msg-user .support-msg-bubble {
  background: linear-gradient(135deg,#4f46e5,#7c3aed);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.support-msg-ai .support-msg-bubble {
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 4px;
}

/* Typing */
.support-typing {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  border-bottom-left-radius: 4px;
}
.support-typing span {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: #a5b4fc;
  animation: typing-bounce 1.2s ease-in-out infinite;
}
.support-typing span:nth-child(2) { animation-delay: 0.2s; }
.support-typing span:nth-child(3) { animation-delay: 0.4s; }
@keyframes typing-bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

/* Input */
.support-input-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid #e5e7eb;
  background: #fff;
  flex-shrink: 0;
}
.support-input {
  flex: 1;
  resize: none;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 8px 11px;
  font-size: 0.82rem;
  line-height: 1.4;
  outline: none;
  max-height: 100px;
  overflow-y: auto;
  transition: border-color 0.15s;
  font-family: inherit;
}
.support-input:focus { border-color: #7c3aed; }
.support-send-btn {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg,#4f46e5,#7c3aed);
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.15s;
}
.support-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.support-send-btn:not(:disabled):hover { opacity: 0.9; }

/* Transition */
.chat-pop-enter-active { animation: chat-pop-in 0.22s cubic-bezier(0.34,1.56,0.64,1); }
.chat-pop-leave-active { animation: chat-pop-in 0.15s ease-in reverse; }
@keyframes chat-pop-in {
  from { opacity: 0; transform: scale(0.85) translateY(12px); transform-origin: bottom right; }
  to   { opacity: 1; transform: scale(1)   translateY(0);     transform-origin: bottom right; }
}
</style>
